import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/AuthContext";
import { useOnboard } from "../../hooks/OnboardContext";
import { i18n } from "../../localization/i18n";
import api from "../../services/api";
import {
  Container,
  MainChartContainer,
  ChartArea,
  ChartTitle,
  TopLayoutGrid,
  MainMetricsCard,
  SideMetricsGrid,
  MainMetricsInfo,
  MainMetricsChart,
  ChartTitleRow,
  ViewAllLink,
  LeftColumnContainer,
  MobileActionsWrapper,
  DesktopActionsWrapper,
  MainMetricTextContent,
} from "./styles";
import { GiWireCoil } from "react-icons/gi";
import { NumberCardBox } from "./components/NumberCardBox";
import { FiUsers, FiArrowRight } from "react-icons/fi";
import { MdOutlineAttachMoney } from "react-icons/md";
import { RiShoppingCart2Line } from "react-icons/ri";
import { IFilter } from "../Orders/dtos";
import { startOfYear, endOfYear } from "date-fns";
import { DateDropdownFilter } from "../../components/DateDropdownFilter";
import { showToast } from "../../components/CustomToast";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../../components/PrimaryButton";
import { LockedFeatureIcon } from "../../components/LockedFeatureIcon";
import { Skeleton } from "@mui/material";
import { roles } from "../../utils/constants";
import Chart from "react-apexcharts";

export interface IReportResponse {
  day: number | null;
  lastSevenDays: number[] | null;
  yearByMonth: number[] | null;
  ordersCount: number | null;
  patientsLeadsCount: number;
  filamentMaterialConsumption: number | null;
  revenueGenerated: number | null;
}

const MOCK_SEVEN_DAYS = [3, 5, 2, 6, 4, 7, 5];
const MOCK_YEAR_BY_MONTH = [14, 20, 18, 25, 22, 30, 28, 35, 40, 38, 45, 50];
const LOCKED_METRICS_TOOLTIP = "Desbloqueie tornando-se um Licenciado";

export function Dashboard() {
  const navigate = useNavigate();
  const { setNavigationAction, user, isFixiter: authIsFixiter } = useAuth();
  const { licensee, isFixiter: onboardIsFixiter } = useOnboard();
  const isFixiter = onboardIsFixiter || authIsFixiter || user?.role === roles.fixiter;

  const hasProviderId =
    !!licensee?.provider_id ||
    !!(user as any)?.person?.licensee?.provider_id ||
    !!(user as any)?.licensee?.provider_id;

  const [language, setLanguage] = useState("pt-BR");
  const [filter, setFilter] = useState<IFilter>({
    period: { from: startOfYear(new Date()), to: endOfYear(new Date()) },
  });
  const [dashboardData, setDashboardData] = useState<IReportResponse>();
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Rules:
  // - Filament consumption is locked for all Fixiters (no 3D printer) or if backend explicitly returns null
  // - Orders data (today, 7 days, monthly chart, orders count, revenue) is locked for Fixiter without provider or if backend explicitly returns null
  const isFilamentLocked = isFixiter || dashboardData?.filamentMaterialConsumption === null;
  const isOrdersDataLocked = (isFixiter && !hasProviderId) || dashboardData?.ordersCount === null;

  const handleNewOrder = () => {
    if (isFixiter && !hasProviderId) {
      window.open("http://loja.usefixit.com.br/", "_blank");
    } else {
      navigate("/solutions");
    }
  };

  useEffect(() => {
    async function getDashboardData() {
      setLoading(true);
      const queryList = [];

      queryList.push(`language=${language}`);

      if (filter?.period?.from !== undefined && filter?.period?.to !== undefined) {
        queryList.push(`from=${filter.period.from.toJSON()}`);
        queryList.push(`to=${filter.period.to.toJSON()}`);
      }

      try {
        const response = await api.get(`/reports?${queryList.join("&")}`);
        setDashboardData(response.data);
      } catch (error) {
        showToast({
          type: "error",
          message: `${i18n.t("dashboard.error")}`,
        });
      } finally {
        setLoading(false);
      }
    }

    if (filter?.period?.from !== undefined) {
      getDashboardData();
    }
  }, [filter, language]);

  useEffect(() => {
    const currentLanguage = localStorage.getItem("i18nextLng");
    if (currentLanguage === null) {
      return;
    } else if (currentLanguage.startsWith("pt")) {
      setLanguage("pt-BR");
    } else if (currentLanguage.startsWith("es")) {
      setLanguage("es");
    } else if (currentLanguage.startsWith("en")) {
      setLanguage("en");
    }
  }, []);

  useEffect(() => {
    setNavigationAction({
      hasButton: false,
      description: `Olá, ${user?.person?.name?.split(' ')[0] || (isFixiter ? "Fixiter" : user?.role === roles.admin ? "Admin" : "Licenciado")}`,
      customElement: !isMobile ? (
        <DesktopActionsWrapper>
          <DateDropdownFilter
            defaultOption="this_year"
            defaultPeriod={{ from: startOfYear(new Date()), to: endOfYear(new Date()) }}
            onApply={(period) => {
              setFilter({ ...filter, period });
            }}
          />
          {user?.role !== roles.admin && user?.role !== roles.creator && (
            <PrimaryButton 
              text="Novo Pedido" 
              showPlusIcon 
              onClick={handleNewOrder} 
              style={{ height: '38px', borderRadius: '20px', whiteSpace: 'nowrap' }}
            />
          )}
        </DesktopActionsWrapper>
      ) : undefined
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter, isFixiter, hasProviderId, isMobile]);

  const optionsYearByMonth = {
    colors: ["#9835D1"],
    labels: [
      i18n.t("dashboard.months.jan"),
      i18n.t("dashboard.months.feb"),
      i18n.t("dashboard.months.mar"),
      i18n.t("dashboard.months.apr"),
      i18n.t("dashboard.months.may"),
      i18n.t("dashboard.months.jun"),
      i18n.t("dashboard.months.jul"),
      i18n.t("dashboard.months.aug"),
      i18n.t("dashboard.months.sep"),
      i18n.t("dashboard.months.oct"),
      i18n.t("dashboard.months.nov"),
      i18n.t("dashboard.months.dec"),
    ],
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      foreColor: "var(--gray)",
      fontFamily: "inherit",
    },
    grid: {
      show: true,
      borderColor: 'rgba(0,0,0,0.05)',
      strokeDashArray: 4,
    },
    dataLabels: {
      enabled: !isOrdersDataLocked,
      style: {
        colors: undefined,
      },
      background: {
        enabled: true,
        borderColor: undefined,
        dropShadow: {
          enabled: false,
        },
      },
    },
    tooltip: {
      enabled: !isOrdersDataLocked,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '50%',
      }
    },
    yaxis: {
      show: true,
    },
  };

  const seriesYearByMonth = [
    { 
      name: "Pedidos", 
      data: isOrdersDataLocked || !dashboardData?.yearByMonth
        ? MOCK_YEAR_BY_MONTH 
        : dashboardData.yearByMonth 
    }
  ];

  const currentYear = new Date().getFullYear();

  let lastSevenDaysLabel = [] as string[];

  for (
    let removeDaysQuantity = 1;
    removeDaysQuantity <= 7;
    removeDaysQuantity++
  ) {
    const date = new Date(
      new Date().setDate(new Date().getDate() - removeDaysQuantity)
    );
    const dateFormatted = date.toLocaleDateString(language, {
      day: "2-digit",
      month: "2-digit",
    });
    lastSevenDaysLabel.push(dateFormatted);
  }

  // Reverse labels and data to show oldest to newest (left to right)
  const chartLabels = [...lastSevenDaysLabel];
  const reportData = isOrdersDataLocked || !dashboardData?.lastSevenDays
    ? MOCK_SEVEN_DAYS 
    : dashboardData.lastSevenDays;

  const safeData = (reportData || []).map(val => Number(val) || 0);
  const maxSevenDaysValue = Math.max(0, ...safeData);
  const dynamicSevenDaysMax = isOrdersDataLocked
    ? 5
    : maxSevenDaysValue <= 4
    ? 5
    : Math.ceil(maxSevenDaysValue * 1.35);

  const optionsLastSevenDays = {
    colors: ["#9835D1"],
    labels: chartLabels,
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      foreColor: "var(--gray)",
      fontFamily: "inherit"
    },
    grid: {
      show: false,
      padding: {
        top: 0,
        bottom: 0,
        left: 40,
        right: 40
      }
    },
    dataLabels: {
      enabled: !isOrdersDataLocked,
      style: {
        colors: undefined,
      },
      background: {
        enabled: true,
        borderColor: undefined,
        dropShadow: {
          enabled: false,
        },
      },
    },
    tooltip: {
      enabled: !isOrdersDataLocked,
    },
    stroke: {
      curve: 'smooth' as const,
      width: 3,
    },
    fill: {
      type: 'gradient' as const,
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      labels: { 
        show: true,
        offsetY: -2,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      show: false,
      min: 0,
      max: dynamicSevenDaysMax,
    },
  };

  const seriesLastSevenDays = [
    { name: i18n.t("dashboard.requestsSevenDays") || "Pedidos", data: safeData },
  ];

  return (
    <Container>
      <TopLayoutGrid>
        <LeftColumnContainer>
          <MainMetricsCard style={{ position: 'relative' }}>
            {isOrdersDataLocked && (
              <LockedFeatureIcon title={LOCKED_METRICS_TOOLTIP} withOverlay />
            )}

            <MainMetricsInfo>
              <MainMetricTextContent>
                <ChartTitleRow>
                  <ChartTitle style={{ marginBottom: 0, fontSize: '14px', fontWeight: 500, color: 'var(--gray)' }}>
                    Pedidos hoje
                  </ChartTitle>
                  {!isOrdersDataLocked && (
                    <ViewAllLink onClick={() => {
                      if (user?.role === roles.admin) navigate("/admin/orders");
                      else if (user?.role === roles.creator) navigate("/creators/orders");
                      else navigate("/orders");
                    }}>
                      Ver todos <FiArrowRight />
                    </ViewAllLink>
                  )}
                </ChartTitleRow>
                
                <div style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  color: 'var(--fixit)',
                  lineHeight: 1,
                  filter: isOrdersDataLocked ? 'blur(4px)' : 'none',
                  opacity: isOrdersDataLocked ? 0.35 : 1,
                  userSelect: isOrdersDataLocked ? 'none' : 'auto',
                }}>
                  {loading ? (
                    <Skeleton variant="text" width={60} height={48} />
                  ) : (
                    isOrdersDataLocked ? 0 : (dashboardData?.day ?? 0)
                  )}
                </div>
              </MainMetricTextContent>
            </MainMetricsInfo>
            
            <MainMetricsChart style={{
              filter: isOrdersDataLocked ? 'blur(4px)' : 'none',
              opacity: isOrdersDataLocked ? 0.35 : 1,
              userSelect: isOrdersDataLocked ? 'none' : 'auto',
            }}>
              <ChartTitleRow style={{ paddingLeft: '24px', paddingRight: '24px' }}>
                <ChartTitle style={{ marginBottom: 0, fontSize: '14px', fontWeight: 500, color: 'var(--gray)' }}>
                  {`${i18n.t("dashboard.requestsSevenDays")}`}
                </ChartTitle>
              </ChartTitleRow>
              {loading ? (
                <Skeleton variant="rounded" width="100%" height={120} sx={{ margin: '0 24px', width: 'calc(100% - 48px)' }} />
              ) : (
                <Chart
                  options={optionsLastSevenDays}
                  series={seriesLastSevenDays}
                  type="area"
                  height={120}
                />
              )}
            </MainMetricsChart>
          </MainMetricsCard>

          <MobileActionsWrapper>
            {user?.role !== roles.admin && user?.role !== roles.creator && (
              <PrimaryButton 
                text="Novo Pedido" 
                showPlusIcon 
                onClick={handleNewOrder} 
                style={{ height: '38px', borderRadius: '20px' }}
              />
            )}
            <DateDropdownFilter
              defaultOption="this_year"
              defaultPeriod={{ from: startOfYear(new Date()), to: endOfYear(new Date()) }}
              onApply={(period) => {
                setFilter({ ...filter, period });
              }}
            />
          </MobileActionsWrapper>
        </LeftColumnContainer>

        <SideMetricsGrid>
          <NumberCardBox
            icon={<FiUsers />}
            title="Pacientes pelo Localizador"
            value={dashboardData?.patientsLeadsCount ?? 0}
            hasAditionalInfo={true}
            aditionalInfo="Pacientes gerados pelo localizador durante período selecionado"
            isLoading={loading}
            isLocked={false}
          />
          <NumberCardBox
            icon={<MdOutlineAttachMoney />}
            title="Retorno"
            value={isOrdersDataLocked ? "R$ 0" : Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(dashboardData?.revenueGenerated ?? 0)}
            hasAditionalInfo={!isOrdersDataLocked}
            aditionalInfo="Calculado com base na tabela sugerida durante período selecionado"
            isLoading={loading}
            isLocked={isOrdersDataLocked}
            lockedLabel={LOCKED_METRICS_TOOLTIP}
          />
          <NumberCardBox
            icon={<RiShoppingCart2Line />}
            title="Pedidos"
            value={isOrdersDataLocked ? 0 : (dashboardData?.ordersCount ?? 0)}
            hasAditionalInfo={!isOrdersDataLocked}
            aditionalInfo="Pedidos realizados durante período selecionado"
            isLoading={loading}
            isLocked={isOrdersDataLocked}
            lockedLabel={LOCKED_METRICS_TOOLTIP}
          />
          <NumberCardBox
            icon={<GiWireCoil />}
            title="Consumo de Filamento"
            value={isFilamentLocked ? "0g" : `${dashboardData?.filamentMaterialConsumption ?? 0}g`}
            hasAditionalInfo={!isFilamentLocked}
            aditionalInfo="Calculado com base nos pedidos padronizados durante período selecionado"
            isLoading={loading}
            isLocked={isFilamentLocked}
            lockedLabel={LOCKED_METRICS_TOOLTIP}
          />
        </SideMetricsGrid>
      </TopLayoutGrid>

      <MainChartContainer>
        <ChartArea style={{ position: 'relative' }}>
          {isOrdersDataLocked && (
            <LockedFeatureIcon title={LOCKED_METRICS_TOOLTIP} withOverlay />
          )}
          <ChartTitleRow style={{ marginBottom: '8px' }}>
            <ChartTitle style={{ marginBottom: 0, fontSize: '14px', fontWeight: 500, color: 'var(--gray)' }}>
              Pedidos em {currentYear}
            </ChartTitle>
          </ChartTitleRow>
          <div style={{
            filter: isOrdersDataLocked ? 'blur(4px)' : 'none',
            opacity: isOrdersDataLocked ? 0.35 : 1,
            userSelect: isOrdersDataLocked ? 'none' : 'auto',
          }}>
            {loading ? (
              <Skeleton variant="rounded" width="100%" height={280} />
            ) : (
              <Chart
                options={optionsYearByMonth}
                series={seriesYearByMonth}
                type="bar"
                height={280}
              />
            )}
          </div>
        </ChartArea>
      </MainChartContainer>
    </Container>
  );
}
